import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { usePwa } from "../context/PwaContext";
import { useOrders } from "../context/OrdersContext";
import { OrderCard } from "../components/OrderCard";
import { ru } from "../locales/ru";

const inputCls = "w-full h-12 px-4 bg-gray-100 rounded-xl text-base outline-none focus:ring-2 focus:ring-black";
const emptyForm = { pickup_text: "", destination_text: "", comment: "" };

export default function Orders() {
  const { user } = useAuth();
  const { milestone } = usePwa();
  const { orders, upsert, version, openCount } = useOrders();
  const isDriver = user?.roles?.includes("driver");

  const [form, setForm] = useState(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [myOrder, setMyOrder] = useState(null);
  const [open, setOpen] = useState([]);
  const [myActive, setMyActive] = useState([]);
  const [tab, setTab] = useState("open");
  const [history, setHistory] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const view = (o) => orders[o.id] || o;

  const loadLists = useCallback(async () => {
    try {
      const [active, hist] = await Promise.all([
        api.get("/orders/active"),
        api.get("/orders/history", { params: { limit: 10 } }),
      ]);
      setMyOrder(active.data.order);
      if (active.data.order) upsert(active.data.order);
      setHistory(hist.data.orders);
      setHasMore(hist.data.has_more);
      if (isDriver) {
        const [o, m] = await Promise.all([api.get("/orders/open"), api.get("/orders/my-active")]);
        setOpen(o.data);
        setMyActive(m.data);
      }
    } catch {}
  }, [isDriver, upsert]);

  useEffect(() => {
    loadLists();
  }, [loadLists, version]);

  const loadMore = async () => {
    const last = history[history.length - 1];
    if (!last) return;
    try {
      const { data } = await api.get("/orders/history", { params: { before: last.created_at, limit: 10 } });
      setHistory((prev) => [...prev, ...data.orders]);
      setHasMore(data.has_more);
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pickup_text.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/orders", form);
      milestone("order_created");
      upsert(data);
      setMyOrder(data);
      setForm(emptyForm);
      setFormOpen(false);
      toast.success(ru.orders.created);
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    } finally {
      setSaving(false);
    }
  };

  const activeMy = myOrder && (orders[myOrder.id] || myOrder);
  const activeMyLive = activeMy && ["open", "accepted"].includes(activeMy.status) ? activeMy : null;
  const list = tab === "open" ? open.filter((o) => view(o).status === "open") : myActive;

  return (
    <div className="pt-8 pb-28 px-6" data-testid="orders-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight">{ru.nav.orders}</h1>
        {!activeMyLive && (
          <button
            data-testid="new-order-button"
            onClick={() => setFormOpen((v) => !v)}
            className="flex items-center gap-1.5 text-sm font-semibold bg-black text-white px-3.5 py-2.5 rounded-full active:scale-[0.96] transition-transform"
          >
            {formOpen ? <X size={16} /> : <Plus size={16} />} {ru.orders.new}
          </button>
        )}
      </div>

      {formOpen && !activeMyLive && (
        <form onSubmit={submit} className="space-y-2.5 mb-7" data-testid="order-form">
          <input
            data-testid="order-pickup-input"
            value={form.pickup_text}
            maxLength={200}
            onChange={(e) => setForm({ ...form, pickup_text: e.target.value })}
            placeholder={ru.orders.pickup}
            className={inputCls}
          />
          <input
            data-testid="order-destination-input"
            value={form.destination_text}
            maxLength={200}
            onChange={(e) => setForm({ ...form, destination_text: e.target.value })}
            placeholder={ru.orders.destination}
            className={inputCls}
          />
          <input
            data-testid="order-comment-input"
            value={form.comment}
            maxLength={300}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            placeholder={ru.orders.comment}
            className={inputCls}
          />
          <button
            data-testid="order-submit-button"
            type="submit"
            disabled={saving || !form.pickup_text.trim()}
            className="w-full h-12 bg-black text-white rounded-2xl font-medium active:scale-[0.98] transition-transform disabled:opacity-40"
          >
            {ru.orders.publish}
          </button>
        </form>
      )}

      {activeMyLive && (
        <section className="mb-7" data-testid="my-active-order">
          <h2 className="text-base md:text-lg font-bold mb-2.5">{ru.orders.myOrder}</h2>
          <OrderCard order={activeMyLive} />
        </section>
      )}

      {isDriver && (
        <section className="mb-7">
          <div className="flex gap-1.5 mb-3.5 bg-gray-100 p-1 rounded-full">
            {[
              { id: "open", label: `${ru.orders.tabs.open}${openCount ? ` · ${openCount}` : ""}` },
              { id: "mine", label: ru.orders.tabs.mine },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`orders-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex-1 h-9 rounded-full text-sm font-semibold transition-colors ${
                  tab === t.id ? "bg-white shadow-sm" : "text-gray-500"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="space-y-2.5" data-testid={`orders-list-${tab}`}>
            {list.length === 0 && (
              <p className="text-gray-400 text-sm py-4" data-testid="orders-empty">
                {tab === "open" ? ru.orders.emptyOpen : ru.orders.emptyMine}
              </p>
            )}
            {list.map((o) => (
              <OrderCard key={o.id} order={view(o)} />
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-base md:text-lg font-bold mb-2.5">{ru.orders.history}</h2>
        {history.length === 0 && (
          <p className="text-gray-400 text-sm" data-testid="history-empty">
            {ru.orders.emptyHistory}
          </p>
        )}
        <div className="space-y-2.5" data-testid="orders-history-list">
          {history.map((o) => (
            <OrderCard key={o.id} order={view(o)} compact />
          ))}
        </div>
        {hasMore && (
          <button
            data-testid="history-load-more"
            onClick={loadMore}
            className="mt-3 w-full h-11 bg-gray-100 rounded-2xl text-sm font-semibold active:scale-[0.98] transition-transform"
          >
            {ru.orders.loadMore}
          </button>
        )}
      </section>
    </div>
  );
}
